import _ from "lodash";
import React from "react";
import classes from "./CaptureVideo.scss";
import classNames from "classnames";
import ScreenPanel from "../../ScreenPanel";
import I18nHOC from "UIComponents/I18nHOC";
import DialogueBox from "UIComponents/DialogueBox";
import CountdownScreen from "UIComponents/CountdownScreen";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import DialogueBoxV3 from "UIComponents/DialogueBoxV3";
import { compose } from "lib/apolloClient";
import Webcam from "react-webcam";
import {
  generateRandomId,
  formatTime,
  getCompressedBlob,
  isSafari,
} from "Utils";
import { PauseIcon, ResumeIcon, RotateIcon, PlayIcon } from "SvgComponents";
import ScreenHeader from "./ScreenHeader";
import ScreenHeaderV2 from "./ScreenHeaderV2";
import {
  getCameraDeviceIdList,
  checkIfVideoMimeTypeSupported,
  isGlobalHeaderOnMultipleScreensEnabled,
} from "Post/utils";
import { colors } from "Constants";
import {
  setToastMsg,
  setGlobalTrayEnabledState,
  disableGlobalHeader,
  enableGlobalHeader,
} from "Login/modules/LoginModule";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import ACLStore from "lib/aclStore";
import { hideIntercom, showIntercom } from "modules/Services";
import {
  ReloadArrowOutlined,
  CircleFilled,
  PauseFilled,
  PlayFilled,
} from "@toddle-edu/ds-icons";
import { IconButton, Tooltip } from "@toddle-edu/ds-web";
import { getGlobalHeaderWiseScreenRevampStatus } from "Teacher/routes/Courses/routes/Course/modules/CourseModule";
import { isWorksheetWebView } from "Worksheet/modules/WorksheetUtils";

// time limit in milliseconds

const RECORDING_TIME_LIMIT = 600000;
const COUNTDOWN_TIME_LIMIT = 3000;

const FACING_MODE_USER = "user";
const FACING_MODE_ENVIRONMENT = "environment";

// Buttons

const PauseButton = ({ onClick }) => {
  return (
    <div className={classes.pauseButton} onClick={onClick}>
      <PauseIcon width={16} height={20} fill={colors.red} />
    </div>
  );
};

const PauseButtonV2 = ({ onClick }) => {
  const { t } = useTranslation(["common"]);
  return (
    <button
      aria-label={t("common:pause")}
      className={classes.pauseButtonV2}
      onClick={onClick}
    >
      <PauseIcon width={16} height={20} fill={colors.black} />
    </button>
  );
};

const RecordButton = ({ onClick }) => {
  return (
    <div className={classes.recordButton} onClick={onClick}>
      <div className={classes.oval}></div>
    </div>
  );
};

const RecordButtonV2 = ({ onClick }) => {
  const { t } = useTranslation(["common"]);
  return (
    <button
      aria-label={t("common:record")}
      className={classes.recordButtonV2}
      onClick={onClick}
    >
      <div className={classes.square}></div>
    </button>
  );
};

const ResumeButton = ({ onClick }) => {
  return (
    <div className={classes.resumeButton} onClick={onClick}>
      <ResumeIcon width={24} height={22} />
    </div>
  );
};

const ResumeButtonV2 = ({ onClick }) => {
  const { t } = useTranslation(["common"]);
  return (
    <button
      aria-label={t("common:resume")}
      className={classes.resumeButtonV2}
      onClick={onClick}
    >
      <PlayIcon width={24} height={44} fill={"#F75961"} />
    </button>
  );
};

const FlipCameraButton = ({ onClick }) => {
  return (
    <div className={classes.flipCameraButton} onClick={onClick}>
      <RotateIcon />
    </div>
  );
};

const FlipCameraButtonV2 = ({ onClick }) => {
  return (
    <IconButton
      icon={<ReloadArrowOutlined />}
      onClick={onClick}
      variant={"outlined-subtle"}
      size={"medium"}
    />
  );
};

// Screens
const PauseScreen = ({ onClick, showPortfolioParityIcons }) => {
  return (
    <div className={classes.screenContainer}>
      <div className={classes.mediaControls}>
        {showPortfolioParityIcons ? (
          <>
            <PauseButtonV2 onClick={onClick} />
            <PauseButtonV2 onClick={onClick} />
          </>
        ) : (
          <>
            <PauseButton onClick={onClick} />
            <PauseButton onClick={onClick} />
          </>
        )}
      </div>
    </div>
  );
};

const ResumeScreen = ({ onClick, showPortfolioParityIcons }) => {
  return (
    <div className={classes.screenContainer}>
      <div className={classes.mediaControls}>
        {showPortfolioParityIcons ? (
          <>
            <ResumeButtonV2 onClick={onClick} />
            <ResumeButtonV2 onClick={onClick} />
          </>
        ) : (
          <>
            <ResumeButton onClick={onClick} />
            <ResumeButton onClick={onClick} />
          </>
        )}
      </div>
    </div>
  );
};

const RecordScreen = ({
  onClick,
  showFlipCamera = false,
  onFlipCamera,
  showPortfolioParityIcons,
}) => {
  return (
    <div className={classes.screenContainer}>
      <div className={classes.mediaControls}>
        {showPortfolioParityIcons ? (
          <>
            <RecordButtonV2 onClick={onClick} />
            <RecordButtonV2 onClick={onClick} />
          </>
        ) : (
          <>
            <RecordButton onClick={onClick} />
            <RecordButton onClick={onClick} />
          </>
        )}
      </div>
      {showFlipCamera ? (
        <div className={classes.rightSideCameraOptions}>
          <FlipCameraButton onClick={onFlipCamera} />
        </div>
      ) : null}
    </div>
  );
};

const RecordScreenV2 = ({
  t,
  onClick,
  showFlipCamera = false,
  onFlipCamera,
}) => {
  return (
    <div className={classes.actionButtonsContainer}>
      <Tooltip dsVersion="2.0" tooltip={t("common:record")} placement="top">
        <div className={classes.actionButtons}>
          <IconButton
            dsVersion="2.0"
            icon={<CircleFilled variant="critical" />}
            onClick={onClick}
            variant={"neutral"}
            size={"large"}
          />
        </div>
      </Tooltip>
      {showFlipCamera ? (
        <Tooltip
          dsVersion="2.0"
          tooltip={t("common:flip_camera")}
          placement="top"
        >
          <div className={classes.actionButtons}>
            <IconButton
              dsVersion="2.0"
              icon={<ReloadArrowOutlined />}
              onClick={onFlipCamera}
              variant={"neutral"}
              size={"large"}
            />
          </div>
        </Tooltip>
      ) : null}
    </div>
  );
};

const PauseScreenV2 = ({ t, onClick }) => {
  return (
    <div className={classes.actionButtonsContainer}>
      <Tooltip dsVersion="2.0" tooltip={t("common:pause")} placement="top">
        <div
          className={classNames(classes.actionButtons, classes.pauseBackground)}
        >
          <IconButton
            dsVersion="2.0"
            icon={<PauseFilled variant="on" />}
            onClick={onClick}
            variant={"neutral"}
            size={"large"}
          />
        </div>
      </Tooltip>
    </div>
  );
};

const ResumeScreenV2 = ({ t, onClick }) => {
  return (
    <div className={classes.actionButtonsContainer}>
      <Tooltip dsVersion="2.0" tooltip={t("common:resume")} placement="top">
        <div className={classes.actionButtons}>
          <IconButton
            dsVersion="2.0"
            icon={<PlayFilled variant="critical" />}
            onClick={onClick}
            variant={"neutral"}
            size={"large"}
          />
        </div>
      </Tooltip>
    </div>
  );
};

const style = {
  headerTextStyle: {
    color: colors.textDefault,
    marginLeft: "4px",
  },
  captureVideoContainer: {
    padding: "18px",
  },
  captureVideoContainerV2: {
    height: "100vh",
  },
};

const BLANK_OBJECT = {};

const CAPTURE_MIME_TYPE_WEBM = "video/webm;codecs=vp8,opus";
const CAPTURE_MIME_TYPE_MP4 = "video/mp4";

const isWebmSupported = checkIfVideoMimeTypeSupported("video/webm");

const audioConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
};

class CaptureVideo extends React.PureComponent {
  constructor(props) {
    super(props);
    this.webcamRef = React.createRef(null);
    this.mediaRecorder = null;
    this.timerInstance = null;
    this.chunks = [];
    this.mediaOptions = {
      mimeType: isWebmSupported
        ? CAPTURE_MIME_TYPE_WEBM
        : CAPTURE_MIME_TYPE_MP4,
    };
    this.state = {
      recordingState: "record",
      timer: 0,
      showExitDialog: false,
      facingMode: FACING_MODE_USER,
      hasMultipleCameras: false,
      isLoading: true,
      cameraDeviceIds: [],
      currentDeviceId: "",
    };
  }

  componentDidMount() {
    const {
      screenPanelProps: { updatePost },
      setGlobalTrayEnabledState,
      hideIntercom,
      hideGlobalTray = true,
      disableGlobalHeader,
    } = this.props;

    this.getAndUpdateCameraDeviceList();
    updatePost({ attachments: [] });
    if (hideGlobalTray) {
      setGlobalTrayEnabledState(false);
    }
    hideIntercom();
    disableGlobalHeader();
  }

  componentDidUpdate(prevProps) {
    // If canAutoSubmitNow prop changes from false to true, stop the capture
    if (
      prevProps.canAutoSubmitNow !== this.props.canAutoSubmitNow &&
      this.props.canAutoSubmitNow
    ) {
      this.handleStopCaptureClick();
    }
  }

  getAndUpdateCameraDeviceList = () => {
    const { setToastMsg } = this.props;
    getCameraDeviceIdList()
      .then(devices => {
        const isPresent = _.size(devices) > 1;
        this.setState({
          hasMultipleCameras: isPresent,
          isLoading: false,
          facingMode: isPresent ? FACING_MODE_ENVIRONMENT : FACING_MODE_USER,
          cameraDeviceIds: devices,
          currentDeviceId: _.get(devices, "[0]", ""),
        });
      })
      .catch(e => {
        setToastMsg({ msg: "toastMsgs:something_went_wrong" });
        this.setState({ isLoading: false });
      });
  };

  flipCamera = () => {
    const isCameraDeviceSwitchingEnabled = ACLStore.can(
      "FeatureFlag:CameraDeviceSwitching"
    );

    if (this.state.hasMultipleCameras) {
      const { cameraDeviceIds, currentDeviceId } = this.state;
      const currentIndex = _.findIndex(
        cameraDeviceIds,
        deviceId => deviceId == currentDeviceId
      );
      const numberOfCameras = _.size(cameraDeviceIds);
      if (
        numberOfCameras == 1 &&
        _.get(cameraDeviceIds, "0") == currentDeviceId &&
        currentDeviceId &&
        isCameraDeviceSwitchingEnabled
      ) {
        return;
      }
      const nextIndex =
        currentIndex == -1 || currentIndex == numberOfCameras - 1
          ? 0
          : currentIndex + 1;
      this.setState(({ facingMode }) => ({
        facingMode:
          facingMode === FACING_MODE_USER
            ? FACING_MODE_ENVIRONMENT
            : FACING_MODE_USER,
        currentDeviceId: cameraDeviceIds[nextIndex],
      }));
    }
  };

  getRecordingTimeLimit = () => {
    const { recordingTimeLimit } = this.props;
    return _.isNumber(recordingTimeLimit) && recordingTimeLimit > 0
      ? recordingTimeLimit
      : RECORDING_TIME_LIMIT;
  };

  startTimer = () => {
    const timeLimit = this.getRecordingTimeLimit();
    this.timerInstance = setInterval(() => {
      // close recording after recording limit reached
      if (this.state.timer == timeLimit / 1000) this.handleStopCaptureClick();
      else this.setState(state => ({ timer: state.timer + 1 }));
    }, 1000);
  };

  stopTimer = () => {
    clearInterval(this.timerInstance);
  };

  componentWillUnmount() {
    const {
      setGlobalTrayEnabledState,
      showIntercom,
      hideGlobalTray = true,
      enableGlobalHeader,
    } = this.props;
    this.setState({ recordingState: "stopped" });
    clearInterval(this.timerInstance);
    if (hideGlobalTray) {
      setGlobalTrayEnabledState(true);
    }
    showIntercom();

    isGlobalHeaderOnMultipleScreensEnabled() && enableGlobalHeader();
  }

  handleStartCaptureClick = () => {
    this.setState({ recordingState: "recording" });

    // init Mediarecorder
    try {
      this.mediaRecorder = new MediaRecorder(
        this.webcamRef.current.stream,
        this.mediaOptions
      );

      // Add event listener to listen when player stops and dataavailable event triggered
      this.mediaRecorder.ondataavailable = this.onDataAvailable;

      // Add event listener to listen when stop event triggered
      this.mediaRecorder.onstop = this.onStop;

      this.mediaRecorder.start();

      // start timer
      this.startTimer();
    } catch {}
  };

  onStop = async () => {
    const {
      screenPanelProps: {
        updatePost,
        goToStep,
        postDetails: { attachments = [] },
      },
    } = this.props;

    const data = new Blob(this.chunks, { type: this.mediaOptions.mimeType });

    if (data.size > 0 && this.webcamRef.current) {
      // generate upload id
      const uploadId = generateRandomId();

      // get video resolution
      let vidHeight = 720;
      let vidWidth = 1280;

      if (_.get(this.webcamRef, "current.stream", null)) {
        const webcamSettings = this.webcamRef.current.stream
          ?.getVideoTracks?.()?.[0]
          ?.getSettings?.();
        vidHeight = _.get(webcamSettings, "height", vidHeight);
        vidWidth = _.get(webcamSettings, "width", vidWidth);
      }

      const { blob: compressedBlob, mimeType } = await getCompressedBlob({
        chunks: _.cloneDeep(this.chunks),
        mimeType: isWebmSupported
          ? CAPTURE_MIME_TYPE_WEBM
          : CAPTURE_MIME_TYPE_MP4,
      });

      // create blob url from blob
      const blobUrl = URL.createObjectURL(compressedBlob, {
        type: mimeType,
      });

      // file name
      const timestamp = moment().format("DD-MM-YY_h_mm");
      const filename = "toddle_video_".concat(timestamp);
      // create file object
      const file = new File([compressedBlob], filename, {
        type: mimeType,
        lastModified: Date.now(),
      });

      const attachment = {
        file: file,
        id: uploadId,
        metadata: {
          height: vidHeight,
          size: compressedBlob.size,
          width: vidWidth,
        },
        mimeType,
        name: filename,
        type: "VIDEO",
        url: blobUrl,
      };

      // update redux store and move to next step
      updatePost({ attachments: [...attachments, attachment], uploadId });
      if (goToStep) goToStep("POST_DETAIL");
    }
  };

  onDataAvailable = ({ data }) => {
    if (data) this.chunks.push(data);
  };

  handlePauseCaptureClick = () => {
    // pause timer
    this.stopTimer();
    if (this.mediaRecorder?.state !== "inactive") {
      this.mediaRecorder?.pause?.();
    }
    this.setState({ recordingState: "paused" });
  };

  handleResumeCaptureClick = () => {
    if (this.mediaRecorder?.state !== "inactive") {
      this.mediaRecorder?.resume?.();
    }
    this.setState({ recordingState: "recording" });
    // resume timer
    this.startTimer();
  };

  handleStopCaptureClick = () => {
    // stop timer
    this.stopTimer();
    if (this.mediaRecorder?.state !== "inactive") {
      this.mediaRecorder?.stop?.();
    }
  };

  handleCountdown = () => {
    this.setState({ recordingState: "countdown" });
  };

  getRecordingStateScreen = () => {
    const { recordingState, hasMultipleCameras } = this.state;
    const isSafariOrNot = isSafari();

    switch (recordingState) {
      case "record":
        return (
          <RecordScreen
            onClick={this.handleCountdown}
            onFlipCamera={this.flipCamera}
            showFlipCamera={hasMultipleCameras && !this.props?.showDesignV2}
            showPortfolioParityIcons={this.props?.showDesignV2}
          />
        );
      case "countdown":
      case "recording":
        if (isSafariOrNot && !this.props?.showDesignV2) {
          return;
        }
        if (this.state.timer < 1) return; // This is to avoid showing the pause screen when recording is not started yet
        return (
          <PauseScreen
            onClick={
              this.props?.showDesignV2 && _.isEqual(recordingState, "countdown")
                ? _.noop
                : this.handlePauseCaptureClick
            }
            showPortfolioParityIcons={this.props?.showDesignV2}
          />
        );
      case "paused":
        if (isSafariOrNot && !this.props?.showDesignV2) {
          return;
        }
        return (
          <ResumeScreen
            onClick={this.handleResumeCaptureClick}
            showPortfolioParityIcons={this.props?.showDesignV2}
          />
        );
    }
  };

  getRecordingStateScreenV2 = () => {
    const { recordingState, hasMultipleCameras } = this.state;
    const { t } = this.props;

    switch (recordingState) {
      case "record":
        return (
          <RecordScreenV2
            t={t}
            onClick={this.handleCountdown}
            onFlipCamera={this.flipCamera}
            showFlipCamera={hasMultipleCameras}
          />
        );
      case "countdown":
      case "recording":
        if (this.state.timer < 1) return; // This is to avoid showing the pause screen when recording is not started yet
        return (
          <PauseScreenV2
            t={t}
            onClick={
              _.isEqual(recordingState, "countdown")
                ? _.noop
                : this.handlePauseCaptureClick
            }
          />
        );
      case "paused":
        return <ResumeScreenV2 t={t} onClick={this.handleResumeCaptureClick} />;
    }
  };

  // Screen Info: It comes with overlay and text over the media controls
  getScreenInfo = () => {
    const { t } = this.props;
    const { recordingState } = this.state;

    switch (recordingState) {
      case "countdown":
        return (
          <CountdownScreen
            timeLimit={COUNTDOWN_TIME_LIMIT}
            callback={this.handleStartCaptureClick}
            showDesignV2={this.props?.showDesignV2}
          />
        );
      case "paused":
        return (
          <div className={classes.screenInfoContainer}>
            <div className={classes.overlay}></div>
            <div className={classes.recordingPausedText}>
              {t("common:state_paused", {
                label: t("common:recording"),
              })}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  onCloseClick = () => {
    const {
      screenPanelProps: { onCloseClick },
    } = this.props;
    const { recordingState } = this.state;
    // enable exit dialog box when its recording else just go back
    if (recordingState != "record") {
      this.setState({ showExitDialog: true });
      // pause recording when user tries to exit
      this.handlePauseCaptureClick();
    } else {
      onCloseClick();
    }
  };

  handleCancelExitDialog = () => {
    this.setState({ showExitDialog: false });
    // resume recording when user drops the idea to exit
    this.handleResumeCaptureClick();
  };

  handleToggleExitDialogBoxDisplay = ({ isCancel } = { isCancel: false }) => {
    if (isCancel) {
      this.setState({ showExitDialog: false });
      // resume recording when user drops the idea to exit by clicking cross icon
      this.handleResumeCaptureClick();
    }
  };

  handleError = e => {
    const { setToastMsg } = this.props;
    const errorMessage = _.get(e, "message", "");
    if (!_.isEmpty(errorMessage)) {
      setToastMsg({
        msg: "toastMsgs:enable_dynamic_permission",
        locale_params: [
          { key: "permission", value: "camera", isPlainText: true },
        ],
      });
    }
    /*if (
      !_.isEmpty(errorMessage) &&
      _.isEqual(errorMessage, "Permission denied")
    ) {
      setToastMsg({
        msg: "toastMsgs:enable_dynamic_permissions",
        locale_params: [
          {
            key: "permissions",
            value: "camera and microphone",
            isPlainText: true,
          },
        ],
      });
    } else {
      setToastMsg({
        msg: "toastMsgs:something_went_wrong",
      });
    }*/
  };

  render() {
    const {
      screenPanelProps,
      t,
      rightHeaderButton = null,
      shouldBeResponsive = false,
    } = this.props;
    const { onCloseClick } = screenPanelProps;
    const {
      recordingState,
      timer,
      showExitDialog,
      isLoading,
      facingMode,
      currentDeviceId,
      hasMultipleCameras,
    } = this.state;
    // progressTimer format is 00:00/10:00
    const timeLimit = this.getRecordingTimeLimit();
    const progressTimer = formatTime(timer).concat(
      "/",
      formatTime(timeLimit / 1000)
    );
    const mirroredOutput = facingMode === FACING_MODE_USER;
    const isCameraDeviceSwitchingEnabled = ACLStore.can(
      "FeatureFlag:CameraDeviceSwitching"
    );
    const videoConstraints = isCameraDeviceSwitchingEnabled
      ? {
          deviceId: currentDeviceId,
        }
      : { facingMode: facingMode };
    const showDesignV2 = this.props?.showDesignV2;
    const isGlobalHeaderEnabled = getGlobalHeaderWiseScreenRevampStatus();
    const isMirrorStreamEnabled = ACLStore.can(
      "FeatureFlag:MirrorCameraStream"
    );
    //using this flag to revamp ui as per the new mirror image design in captureImage component
    const isMirrorImageEnabled = ACLStore.can("FeatureFlag:MirrorCameraImage");
    const webcamStyle = classNames(classes.webcamScreen, {
      [classes.mirrorStreamView]:
        isMirrorStreamEnabled && !isMirrorImageEnabled,
    });

    const isMirrored =
      isCameraDeviceSwitchingEnabled || isMirrorImageEnabled
        ? false
        : mirroredOutput;
    return (
      <ScreenPanel
        {...screenPanelProps}
        containerStyle={{ padding: 0 }}
        showHeader={!this.props?.showDesignV2}
      >
        {isLoading && <FullScreenLoader />}
        {showDesignV2 ? (
          <ScreenHeaderV2
            isRecordingPaused={recordingState == "paused"}
            onBackClick={this.onCloseClick}
            onDoneClick={this.handleStopCaptureClick}
            isDoneDisabled={recordingState == "record" || this.state.timer < 1}
            showTimer={recordingState != "record"}
            timer={progressTimer}
            title={t("common:capture_video")}
            showFlipCamera={hasMultipleCameras && !isMirrorImageEnabled}
            flipCamera={<FlipCameraButtonV2 onClick={this.flipCamera} />}
            isGlobalHeaderEnabled={isGlobalHeaderEnabled}
            rightHeaderButton={rightHeaderButton}
            shouldBeResponsive={shouldBeResponsive}
          />
        ) : (
          <ScreenHeader
            isRecordingPaused={recordingState == "paused"}
            onBackClick={this.onCloseClick}
            onDoneClick={this.handleStopCaptureClick}
            isDoneDisabled={recordingState == "record" || this.state.timer < 1}
            showTimer={recordingState != "record"}
            timer={progressTimer}
          />
        )}
        <div
          className={classes.captureVideoContainer}
          style={showDesignV2 ? style.captureVideoContainerV2 : BLANK_OBJECT}
        >
          <Webcam
            className={webcamStyle}
            ref={this.webcamRef}
            audio={true}
            audioConstraints={audioConstraints}
            imageSmoothing={true}
            onUserMedia={() => {
              /**
               * Re-fetch camera device list after user grants permission to ensure
               * camera flip button visibility in WebView environments.
               * WebView sometimes grants permissions after component mounting,
               * which can cause the camera selection UI to be missing initially.
               */
              if (isWorksheetWebView()) {
                this.getAndUpdateCameraDeviceList();
              }
            }}
            onUserMediaError={this.handleError}
            mirrored={isMirrored}
            videoConstraints={videoConstraints}
            muted
          />
          {isMirrorImageEnabled
            ? this.getRecordingStateScreenV2()
            : this.getRecordingStateScreen()}
          {this.getScreenInfo()}

          {/* If user clicked back without saving */}
          {showExitDialog ? (
            this.props?.showDesignV2 ? (
              <DialogueBoxV3
                modalTitle={t("common:capture_video")}
                showModal={true}
                onClickButton1={this.handleCancelExitDialog}
                onClickButton2={onCloseClick}
                modalBody={t("common:exit_without_save_prompt")}
                toggleDialogueBoxDisplay={this.handleToggleExitDialogBoxDisplay}
                button1={t("common:cancel")}
                button2={t("common:yes")}
                button1Props={{ variant: "neutral", type: "outlined" }}
              ></DialogueBoxV3>
            ) : (
              <DialogueBox
                modalTitle={t("common:capture_video")}
                showModal={true}
                onClickButton1={this.handleCancelExitDialog}
                onClickButton2={onCloseClick}
                modalBody={t("common:exit_without_save_prompt")}
                toggleDialogueBoxDisplay={this.handleToggleExitDialogBoxDisplay}
                button1={t("common:cancel")}
                button2={t("common:yes")}
              ></DialogueBox>
            )
          ) : null}

          {/* If Media recorder is not supported by the browser show this */}
          {!window.hasOwnProperty("MediaRecorder") && (
            <DialogueBox
              modalTitle={t("common:capture_video")}
              showModal={true}
              onClickButton2={onCloseClick}
              toggleDialogueBoxDisplay={onCloseClick}
              modalBody={t("common:functionality_unsupported_browser", {
                label: "Chrome or Firefox",
              })}
              button2={t("common:okay")}
            ></DialogueBox>
          )}
        </div>
      </ScreenPanel>
    );
  }
}

const mapActionCreators = {
  setToastMsg,
  setGlobalTrayEnabledState,
  hideIntercom,
  showIntercom,
  disableGlobalHeader,
  enableGlobalHeader,
};

const mapStateToProps = (state, ownProps) => {
  return {};
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  I18nHOC({ resource: "common" })
)(CaptureVideo);
